import { makeAutoObservable, toJS } from 'mobx'

import { getOrCreateAviaSearchId } from 'entities/ticket/lib/avia-search-id'

import { SearchApi } from 'shared/api/SearchApi'
import { GenerateToken, getGeneratedToken } from 'shared/lib/generation-token'
import { TicketItem } from 'shared/models/ticket'
import { trackAdjustEvent } from 'shared/services/adjust'

import findTicketStore from '../find-ticket/find-ticket-store'
import { IFindTicketForm } from '../find-ticket/models'
import { TYPE_TIMER } from '../timer'
import timerStore from '../timer/timer-store'

import { RequestFindTicket } from './model'

const ITEM_PAGE = 30

interface ParamsSearch {
  isDifficultRoute?: boolean
}

interface SearchAvia {
  params?: ParamsSearch
  form: IFindTicketForm
}

interface SelectedTicket {
  ticket: TicketItem
  tarrifTicket?: TicketItem
}

let generatedToken: GenerateToken = {
  generatedToken: '',
  modifiedToken: '',
}

class ListTicketsStore {
  listOfTickets: TicketItem[] | [] = []

  abortController: AbortController | null = null

  loading: boolean = false

  maxPage: number = 1

  page: number = 1

  totalItems: number | null = null

  selectedTicket: SelectedTicket | null = null

  activeTicketIndex: number | null = null

  constructor() {
    makeAutoObservable(this)
  }

  async onGetTicketRequest({ params, form }: SearchAvia) {
    const requestData: RequestFindTicket = { ...form }

    requestData.page = this.page
    requestData.per_page = ITEM_PAGE
    const isFirstPage = requestData.page === 1
    const searchId = isFirstPage ? getOrCreateAviaSearchId(form) : null

    if (this.loading) return

    timerStore.onResetTimer(TYPE_TIMER.RESULT_LIST)

    try {
      if (isFirstPage) {
        generatedToken = getGeneratedToken()
        this.abortController = new AbortController()
        this.activeTicketIndex = null
      }

      this.loading = true
      const { data: ticketsData } = await SearchApi.searchTicket(
        {
          ...requestData,
          searchParams: {
            isDifficultSearch: Boolean(params?.isDifficultRoute),
          },
        },
        generatedToken,
        this.abortController?.signal,
      )

      if (searchId) {
        void trackAdjustEvent('aviaSearch', searchId)
      }

      if (ticketsData?.data?.length) {
        timerStore.startTimer(TYPE_TIMER.RESULT_LIST)
      }

      this.loading = false
      this.maxPage = ticketsData.total_pages || 1
      this.totalItems = ticketsData.total_items ?? this.totalItems
      this.page += 1

      findTicketStore.setDataForFilter({
        priceRange: ticketsData.price_range,
        airlines: ticketsData.airlines,
        airports: ticketsData.airports,
        timeline: ticketsData.nearby_price,
        transferTimer: ticketsData.transfer_duration,
      })

      this.listOfTickets = [...(this.listOfTickets || []), ...ticketsData.data]
    } catch (err) {
      this.loading = false
    }
  }

  setActiveTicketIndex(index: number) {
    this.activeTicketIndex = index
  }

  setTicketFavoriteByKey(key: string, isFavorite: boolean) {
    this.listOfTickets = this.listOfTickets.map((ticket) =>
      ticket.favorite_search_key === key
        ? {
            ...ticket,
            is_favorite: isFavorite,
          }
        : ticket,
    )

    if (this.selectedTicket?.ticket?.favorite_search_key === key) {
      this.selectedTicket = {
        ...this.selectedTicket,
        ticket: {
          ...this.selectedTicket?.ticket,
          is_favorite: isFavorite,
        },
      }
    }
  }

  getListTickets() {
    return toJS(this.listOfTickets)
  }

  getTicketByIndex(index: number | null) {
    return [toJS(this.listOfTickets[index ?? 0])]
  }

  setSelectedTicket(ticket: SelectedTicket) {
    this.selectedTicket = ticket
  }

  getParentTicket() {
    return toJS(this.selectedTicket?.ticket)
  }

  getSelectedTicket() {
    if (!this.selectedTicket) return null

    if (this.selectedTicket?.tarrifTicket) {
      return toJS(this.selectedTicket?.tarrifTicket)
    }

    return toJS(this.selectedTicket?.ticket)
  }

  onNewListOfTicket(params?: ParamsSearch) {
    const formFindTicket = toJS(findTicketStore.formFindTicket)

    this.resetListOfTicket()

    this.onGetTicketRequest({
      form: formFindTicket,
      params,
    })

    findTicketStore.resetDataForFilter()
  }

  resetListOfTicket() {
    this.listOfTickets = []
    this.selectedTicket = null
    this.page = 1
    this.maxPage = 1
    this.totalItems = null
    this.loading = false

    timerStore.onResetTimer(TYPE_TIMER.RESULT_LIST)
    findTicketStore.resetDataForFilter()

    if (this.abortController) {
      this.abortController.abort()
    }
  }
}

export default new ListTicketsStore()
